import { startCase, truncate } from 'lodash';
// @ts-ignore
import { css } from '@goodrx/matisse';
import qs from 'qs';
import Settings from 'app/config/settings';
import { Address } from 'app/types';

export const isServer = () => typeof window === 'undefined';
export const isClient = () => !isServer();

/* eslint-disable camelcase */
export const prettyAddress = (obj: Address = {}) => {
  const { addressLine1, addressLine2 = '', city, state, zip } = obj;

  return `${startCase(addressLine1)}${startCase(addressLine2) ? ` \r${startCase(addressLine2)}` : ''}\r${startCase(
    city
  )}, ${startCase(state)} ${startCase(zip)}`;
};

export const prettyPhone = (phoneNo: string | null = '') => {
  if (!phoneNo) {
    return phoneNo;
  }
  const numbers = `${phoneNo}`.replace(/\D/g, '');
  const [, areaCode, prefix, lineNo] = numbers.match(/^(\d{3})(\d{3})(\d{4})$/) as any;
  return !areaCode || !prefix || !lineNo ? '' : `(${areaCode}) ${prefix}-${lineNo}`;
};

export const isMobileOrTablet = () =>
  /iphone|ipad|ipod|android|iemobile|blackberry/i.test(navigator.userAgent.toLowerCase());

/**
 * Executes an async request while managing state loading
 * @param  {String} [key='loading'] [description]
 * @param  {Function} request         [description]
 * @example
 *   performRequestLifecycle({
 *     context: this,
 *     loadingKey: 'loadingStuff',
 *     work: () => this.props.dispatch(deletePrescription(this.props.visit.id, id))
 *   });
 */
export async function performRequestLifecycle({
  context,
  loadingKey = 'loading',
  errorKey = 'error',
  work = () => null,
}: any) {
  context.setState({ [loadingKey]: true, [errorKey]: null });
  try {
    return await work();
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error(error); // eslint-disable-line no-console
    }
    context.setState({ [errorKey]: error });
    return error;
  } finally {
    context.setState({ [loadingKey]: false });
  }
}

const EMAIL_REGEX = /^[a-zA-Z0-9+_.-]+@[a-zA-Z0-9.-]+$/;
export function isValidEmail(email: any) {
  return EMAIL_REGEX.test(email);
}

export const isInternetExplorer = () => {
  const ua = typeof window !== 'undefined' && window?.navigator?.userAgent; // Check the userAgent property of the window.navigator object
  const msie = ua && ua.indexOf('MSIE '); // IE 10 or older
  const trident = ua && ua.indexOf('Trident/'); // IE 11

  return msie > 0 || trident > 0;
};

export const getQueryParams = () => {
  const search = typeof window !== 'undefined' && window.location.search.replace(/^\?/, '');
  return (search && qs.parse(search)) || {};
};

// eslint-disable-next-line @typescript-eslint/naming-convention
export const isHttp4XXError = (msg: any) => !!msg.match(/failed with status code 4\d\d$/g);

export const getServiceCost = (service: any) => {
  const retailCost = service.retailCost || service.retail_cost || 0;
  const fixedCost = service.fixedCost || service.fixed_cost || 0;

  return (retailCost + fixedCost) / 100;
};

export const wordsCount = (text = '') => text.split(' ').length;
export const truncateWords = (text = '', words = 50) => {
  if (wordsCount(text) < words) {
    return text;
  }

  return `${text.split(' ').slice(0, words).join(' ')}...`;
};

export const truncateText = (text: any, length = 25) => truncate(text, { length });

export const mediaMinWidth = (styles: any) => ({ theme }: any) =>
  Object.keys(styles).map(
    (breakpoint) => css`
      @media (min-width: ${theme.breakpoints[breakpoint]}) {
        ${styles[breakpoint]};
      }
    `
  );

export const mediaMaxWidth = (styles: any) => ({ theme }: any) =>
  Object.keys(styles).map(
    (breakpoint) => css`
      @media (max-width: ${theme.breakpoints[breakpoint]}) {
        ${styles[breakpoint]};
      }
    `
  );

if (typeof navigator !== 'undefined') {
  // from: https://developer.mozilla.org/en-US/docs/Web/API/MediaDevices/getUserMedia
  // Older browsers might not implement mediaDevices at all, so we set an empty object first
  if (navigator.mediaDevices === undefined) {
    // @ts-ignore
    navigator.mediaDevices = {};
  }

  // Some browsers partially implement mediaDevices. We can't just assign an object
  // with getUserMedia as it would overwrite existing properties.
  // Here, we will just add the getUserMedia property if it's missing.
  if (navigator.mediaDevices.getUserMedia === undefined) {
    navigator.mediaDevices.getUserMedia = function userMedia(constraints) {
      // First get ahold of the legacy getUserMedia, if present
      // @ts-ignore
      const getUserMedia = navigator.getUserMedia || navigator.webkitGetUserMedia || navigator.mozGetUserMedia;

      // Some browsers just don't implement it - return a rejected promise with an error
      // to keep a consistent interface
      if (!getUserMedia) {
        return Promise.reject(new Error('getUserMedia is not implemented in this browser'));
      }

      // Otherwise, wrap the call to the old navigator.getUserMedia with a Promise
      return new Promise((resolve, reject) => {
        getUserMedia.call(navigator, constraints, resolve, reject);
      });
    };
  }
}

export const getMediaStream = async (options: any) => navigator.mediaDevices.getUserMedia(options);

export const debugLog = (...args: any) => {
  if (Settings.Dev) {
    // eslint-disable-next-line no-console
    console.log(...args);
  }
};

const getCircularReplacer = () => {
  const seen = new WeakSet();
  return (_: any, value: any) => {
    if (typeof value === 'object' && value !== null) {
      if (seen.has(value)) {
        return undefined;
      }
      seen.add(value);
    }
    return value;
  };
};

export function removeCircularReferences<T>(obj: T): T {
  const replacer = getCircularReplacer();
  const stringified = JSON.stringify(obj, replacer);
  return JSON.parse(stringified);
}

/**
 * Converts a long string of bytes into a readable format e.g KB, MB, GB, TB, YB
 * @param bytes
 */
export const readableBytes = (bytes: number): string => {
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];

  const bytesToSize = bytes / 1024 ** i;

  return `${+bytesToSize.toFixed(2)} ${sizes[i]}`;
};
